import React from 'react';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';

const bootstrap = getBootstrap();
const facebookClientId = bootstrap.get('facebookClientId');

/**
 * Hot observable to emit the Facebook sdk when it's ready
 */
const facebookSdk$ = Observable.create((observer) => {
    // Add the Facebook SDK script if it has not already been added
    if (!window.FB) {
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: facebookClientId,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v9.0',
            });

            // Emit the sdk in the observable when it's ready
            observer.next(window.FB);
        };

        // Asynchonously load facebook javascript SDK
        // This snippet is provided by the SDK documentation and lightly modified by us
        (function (d, s, id) {
            if (d.getElementById(id)) {
                return;
            }
            const js = d.createElement(s);
            js.id = id;
            const fjs = d.getElementsByTagName(s)[0] || d.getElementsByTagName('body')[0];
            js.src = '//connect.facebook.net/en_US/sdk.js';
            fjs.parentNode.insertBefore(js, fjs);
        })(document, 'script', 'facebook-jssdk');
    } else {
        // In case the sdk is already available, just emit it
        observer.next(window.FB);
    }
}).pipe(share());

/**
 * Custom React hook to use the Facebook sdk when it's ready
 */
export const useFacebookSdk = () => {
    const [facebookSdk, setFacebookSdk] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        const facebookSubscription = facebookSdk$.subscribe((fb) => {
            setFacebookSdk(fb);
            setIsLoading(false);
        });

        return () => facebookSubscription.unsubscribe();
    }, []);

    return [facebookSdk, isLoading];
};

/**
 * Promisified Facebook login using the Facebook sdk
 * @param {Object} sdk Facebook sdk
 * @param {String} scope Facebook scope i.e. 'email'
 * @returns A promise solved with the facebook sdk
 */
export const facebookLogin = (sdk, scope = '') =>
    new Promise((resolve, reject) => {
        sdk.login(
            (response) => {
                if (response.authResponse) {
                    resolve(sdk);
                } else {
                    reject({ error: 'user_cancelled', response });
                }
            },
            { scope },
        );
    });

/**
 * Promisified Facebook /me endpoint request
 * @param {Array[String]} fieldList Facebook fields list i.e. 'first_name'
 */
export const facebookMe =
    (fieldList = []) =>
    (sdk) =>
        new Promise((resolve, reject) => {
            const fields = fieldList.join(',');
            sdk.api(`/me?fields=${fields}`, (response) => {
                if (!response || response.error) {
                    reject({ error: 'me_error', response });
                } else {
                    resolve(response);
                }
            });
        });
